import { useState } from "react";
import axios from "axios";
import { Send, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function NewsletterForm({ variant = "light", testIdPrefix = "footer-newsletter" }) {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const isDark = variant === "dark";
  const inputTextColor = isDark ? "text-[#FAF9F6]" : "text-[#1A1B1A]";
  const inputPlaceholder = isDark ? "placeholder:text-[#5a5a5a]" : "placeholder:text-[#9A9A9A]";
  const borderColor = isDark ? "border-[#C4A462]/40" : "border-[#1A1B1A]";
  const buttonColor = isDark ? "text-[#C4A462] hover:text-[#FAF9F6]" : "text-[#1A1B1A] hover:text-[#C4A462]";
  const successColor = isDark ? "text-[#C4A462] border-[#C4A462]/40" : "text-[#C4A462] border-[#C4A462]/40";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const locale = i18n.language?.startsWith("en") ? "en" : "it";
      await axios.post(`${API}/newsletter`, { email, locale });
      setSubmitted(true);
      setEmail("");
    } catch (err) {
      setError(t("footer.newsletter_error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      <AnimatePresence mode="wait">
        {submitted ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            data-testid={`${testIdPrefix}-success`}
            className={`flex items-center justify-center gap-3 py-4 text-sm border-b ${successColor}`}
            style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400 }}
          >
            <Check strokeWidth={1.5} size={18} />
            {t("footer.newsletter_success")}
          </motion.div>
        ) : (
          <motion.form
            key="form"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onSubmit={handleSubmit}
            data-testid={`${testIdPrefix}-form`}
            className="w-full"
          >
            <div className={`flex items-stretch border-b ${borderColor} bg-transparent`}>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={`${testIdPrefix}-email`}
                placeholder={t("footer.newsletter_placeholder")}
                className={`flex-1 bg-transparent px-1 py-3 text-sm ${inputTextColor} ${inputPlaceholder} focus:outline-none`}
                style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid={`${testIdPrefix}-submit`}
                className={`px-2 ${buttonColor} transition-colors duration-300 flex items-center gap-2 text-xs uppercase tracking-[0.15em] disabled:opacity-50`}
                style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
              >
                {submitting ? "..." : (
                  <>
                    <span className="hidden sm:inline">{t("footer.newsletter_subscribe")}</span>
                    <Send strokeWidth={1.5} size={16} />
                  </>
                )}
              </button>
            </div>
            {error && (
              <p data-testid={`${testIdPrefix}-error`} className={`text-xs mt-3 text-left ${isDark ? "text-red-400" : "text-red-600"}`}>
                {error}
              </p>
            )}
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}
